/**
 * 添付・領収書のプレビュー(ポップアップ)。別タブは開かず画面中央に表示する。
 * 画像はピンチ・ホイールで拡大縮小、PDFは pdf.js でアプリ内に描画する(2026-08-31)。
 * スマホでは画面いっぱいに表示する(枠内に小さく収まらないように)。
 *
 * レイアウトはインラインスタイルで完結させる(表のセル内から呼ばれても崩れないように)。
 */
import { useEffect, useState } from 'react';
import Modal from '@core/components/Modal';
import PdfView from '@core/components/PdfView';
import ZoomableImage from '@core/components/ZoomableImage';
import { useIsMobile } from '@core/lib/useIsMobile';
import { getFileUrl } from './attachments';
import type { StoredFile } from '@core/lib/types';

export default function FilePreviewModal({
  file,
  onClose,
}: {
  file: StoredFile;
  onClose: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    let alive = true;
    getFileUrl(file.path)
      .then((u) => alive && setUrl(u))
      .catch(() => alive && setError('ファイルを読み込めませんでした'));
    return () => {
      alive = false;
    };
  }, [file.path]);

  const isPdf = file.content_type === 'application/pdf';
  // 添付は画面いっぱいに表示する(2026-09-10 指示。PCもスマホも全画面)
  const bodyMaxHeight = '100%';

  return (
    <Modal onClose={onClose} fullscreen>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexShrink: 0, marginBottom: 8 }}>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: isMobile ? 13 : undefined }}>
            {file.name}
          </h3>
          <div className="path" style={{ margin: '2px 0 0' }}>
            {isPdf ? 'PDF' : '画像'} / {Math.round(file.size / 1024).toLocaleString()} KB
          </div>
        </div>
      </div>

      {/* 中身(画像・PDF)は画面の残り高さいっぱいまで使う(.preview-fill) */}
      <div
        className="preview-fill"
        style={{
          minHeight: isMobile ? 0 : 200,
          display: 'flex',
          background: 'var(--paper)',
          border: isMobile ? 'none' : '1px solid var(--line)',
          borderRadius: isMobile ? 0 : 6,
        }}
      >
        {error ? (
          <p className="empty" style={{ margin: 'auto' }}>
            {error}
          </p>
        ) : !url ? (
          <p className="empty" style={{ margin: 'auto' }}>
            読み込み中…
          </p>
        ) : isPdf ? (
          /* スマホのブラウザは iframe のPDFを埋め込めない(「開く」だけが出る)ため、
             pdf.js でアプリ内に描画する(2026-08-31 指示) */
          <PdfView url={url} maxHeight={bodyMaxHeight} />
        ) : (
          <ZoomableImage src={url} alt={file.name} maxHeight={bodyMaxHeight} />
        )}
      </div>
    </Modal>
  );
}
